import React, { useEffect, useRef, useState } from 'react';
import { LogOut, ShieldCheck, ChevronDown, ChevronUp, Star, User } from 'lucide-react';
import type { CustomerRecord } from '../CustomerLogin';
import { BRAND_PORTAL } from '../../constants';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { PortalArbeitskontextBar } from './PortalArbeitskontextBar';
import { PORTAL_SHELL } from './portalTheme';
import { isBetaTester } from './portalCustomerAccess';
import {
  type PortalView,
  type PortalNavContext,
  getPrimaryNavItems,
  getMehrNavItems,
  getAccountNavItems,
  resolveSidebarActiveView,
  isMehrViewActive,
  hasMatrixRoom,
} from './portalNavigation';

export interface PortalSidebarProps {
  customer: Pick<CustomerRecord, 'kanzlei' | 'email' | 'tier' | 'account_kind'>;
  matrixRoomId?: string;
  documentCount?: number | null;
  isPremium: boolean;
  contractGeneratorEnabled?: boolean;
  /** MEMCORE Modul B (Labor): „Mein Gedächtnis“ im Mehr-Menü. */
  memcoreEnabled?: boolean;
  teamAccessEnabled?: boolean;
  view: PortalView;
  onNavigate: (view: PortalView) => void;
  onOpenSettings: () => void;
  onLogout: () => void;
  /** Einstellungs-Panel offen — Account-Eintrag hervorheben. */
  settingsOpen?: boolean;
  mehrExpanded?: boolean;
  onMehrExpandedChange?: (open: boolean) => void;
}

export const PortalSidebar: React.FC<PortalSidebarProps> = ({
  customer,
  matrixRoomId = '',
  documentCount,
  isPremium,
  contractGeneratorEnabled = false,
  memcoreEnabled = false,
  teamAccessEnabled = false,
  view,
  onNavigate,
  onOpenSettings,
  onLogout,
  settingsOpen = false,
  mehrExpanded: mehrExpandedProp,
  onMehrExpandedChange,
}) => {
  const [mehrLocal, setMehrLocal] = useState(() => isMehrViewActive(view));
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const mehrExpanded = mehrExpandedProp ?? mehrLocal;
  const setMehrExpanded = onMehrExpandedChange ?? setMehrLocal;

  const activeView = resolveSidebarActiveView(view);
  const betaTester = isBetaTester(customer.account_kind);
  const ctx: PortalNavContext = {
    isPremium,
    contractGeneratorEnabled,
    memcoreEnabled,
    teamAccessEnabled,
    betaTester,
  };
  const primary = getPrimaryNavItems(ctx);
  const mehrItems = getMehrNavItems(ctx);
  const accountItems = getAccountNavItems();
  const matrixReady = hasMatrixRoom(matrixRoomId);
  const showDokumenteEditorHint =
    resolveSidebarActiveView(view) === 'dokumente' && view === 'document_editor';
  const accountActive = accountOpen || view === 'matrix' || settingsOpen;

  useEffect(() => {
    if (!accountOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [accountOpen]);

  const navBtn = (id: PortalView, icon: React.ElementType, label: string, extras?: React.ReactNode) => {
    const Icon = icon;
    const isActive = activeView === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => onNavigate(id)}
        className={`${PORTAL_SHELL.sidebarNavItem} ${isActive ? PORTAL_SHELL.sidebarNavActive : PORTAL_SHELL.sidebarNavIdle}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
        <span className="truncate">{label}</span>
        {extras}
      </button>
    );
  };

  const handleAccountItem = (id: PortalView) => {
    setAccountOpen(false);
    if (id === 'settings') {
      onOpenSettings();
      return;
    }
    onNavigate(id);
  };

  return (
    <div className={`h-full ${PORTAL_SHELL.sidebar} flex flex-col`}>
      <button
        type="button"
        onClick={() => onNavigate('home')}
        className={`px-4 py-4 w-full text-left ${PORTAL_SHELL.sidebarHeaderBorder} flex items-center gap-3 ${PORTAL_SHELL.sidebarNavIdle} hover:bg-white/[0.03] transition-colors`}
        aria-label="Zur Startseite"
        title="Start"
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/[0.08] overflow-hidden bg-slate-900/80 flex-shrink-0 relative">
          <img
            src="/arkorbit-wappen.png"
            alt="ARKORBIT"
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ transform: 'scale(1.9) translateY(23%)' }}
          />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm leading-tight truncate">{BRAND_PORTAL}</p>
          <p className="text-slate-500 text-[10px] font-medium tracking-wide truncate">Arbeitsplatz</p>
        </div>
      </button>

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto custom-scrollbar">
        {primary.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              className={`${PORTAL_SHELL.sidebarNavItem} ${isActive ? PORTAL_SHELL.sidebarNavActive : PORTAL_SHELL.sidebarNavIdle} ${
                item.id === 'feedback' && betaTester ? 'ring-1 ring-amber-400/30 bg-amber-500/[0.06]' : ''
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
              <span className="truncate">{item.label}</span>
              {item.badge === 'beta' ? (
                <PortalBadge label="β" accent="amber" className="ml-auto !px-1.5 !py-0 text-[9px]" />
              ) : null}
            </button>
          );
        })}

        {showDokumenteEditorHint && (
          <p className="mx-2 mt-2 px-2 py-2 text-[10px] text-slate-500 leading-relaxed border-l border-white/[0.06]">
            ARKO erscheint rechts, sobald Sie ein Dokument im Editor öffnen.
          </p>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => setMehrExpanded(!mehrExpanded)}
            className={`${PORTAL_SHELL.sidebarNavItem} w-full ${
              isMehrViewActive(view) ? PORTAL_SHELL.sidebarNavActive : PORTAL_SHELL.sidebarNavIdle
            }`}
            aria-expanded={mehrExpanded}
          >
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform ${mehrExpanded ? 'rotate-0' : '-rotate-90'}`}
              aria-hidden
            />
            <span>Mehr</span>
          </button>
          {mehrExpanded && (
            <div className="mt-0.5 ml-2 pl-2 border-l border-white/[0.06] space-y-0.5">
              {mehrItems.map((item) => {
                const extras = (
                  <>
                    {item.dot && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400/80" aria-hidden />
                    )}
                    {item.badge === 'neu' && (
                      <PortalBadge label="neu" accent="violet" className="ml-auto !px-1.5 !py-0 text-[9px]" />
                    )}
                    {item.badge === 'beta' && (
                      <PortalBadge label="α" accent="amber" className="ml-auto !px-1.5 !py-0 text-[9px]" />
                    )}
                  </>
                );
                return navBtn(item.id, item.icon, item.label, extras);
              })}
            </div>
          )}
        </div>
      </nav>

      <div className="px-1 py-2 border-t border-white/[0.06] space-y-1 flex-shrink-0">
        <PortalArbeitskontextBar
          customer={customer}
          documentCount={documentCount}
          placement="sidebar-footer"
        />
        <div className="flex items-center justify-center gap-1 text-[9px] text-emerald-500/80 font-medium tracking-wide uppercase px-2 mb-1">
          <ShieldCheck className="w-3 h-3" aria-hidden /> EU · Frankfurt
        </div>
        {isPremium ? (
          <div className="flex items-center justify-center gap-1 text-[10px] text-amber-400/90 px-2 mb-1">
            <Star className="w-3 h-3" aria-hidden /> Premium
          </div>
        ) : null}

        <div ref={accountRef} className="relative px-1">
          {accountOpen && (
            <div
              className="absolute bottom-full left-1 right-1 mb-1 rounded-lg border border-white/[0.08] bg-slate-900 shadow-xl overflow-hidden z-10"
              role="menu"
              aria-label="Konto & Sicherheit"
            >
              <div className="px-3 py-2.5 border-b border-white/[0.06]">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                  Konto & Sicherheit
                </p>
                <p className="text-white text-xs font-medium truncate mt-0.5">{customer.kanzlei}</p>
                <p className="text-slate-500 text-[10px] truncate">{customer.email}</p>
              </div>
              <div className="py-1">
                {accountItems.map((item) => {
                  const Icon = item.icon;
                  const isMatrix = item.id === 'matrix';
                  const isSettings = item.id === 'settings';
                  const isActive =
                    (isMatrix && view === 'matrix') || (isSettings && settingsOpen);
                  const title =
                    isMatrix && !matrixReady
                      ? 'Matrix-Raum wird beim Onboarding eingerichtet'
                      : undefined;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      title={title}
                      onClick={() => handleAccountItem(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-white/[0.06] text-white'
                          : 'text-slate-300 hover:bg-white/[0.04] hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
                      <span className="truncate">{item.label}</span>
                      {isMatrix && !matrixReady ? (
                        <PortalBadge
                          label="Setup"
                          accent="amber"
                          className="ml-auto !px-1.5 !py-0 text-[9px]"
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-white/[0.06] py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-red-400/90 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                >
                  <LogOut className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
                  <span>Abmelden</span>
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setAccountOpen((o) => !o)}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
              accountActive ? PORTAL_SHELL.sidebarNavActive : PORTAL_SHELL.sidebarNavIdle
            }`}
            aria-expanded={accountOpen}
            aria-haspopup="menu"
            aria-label="Konto & Sicherheit"
          >
            <div className="w-7 h-7 rounded-md bg-slate-800 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-slate-400" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-medium truncate">{customer.kanzlei}</p>
              <p className="text-slate-500 text-[10px] truncate">Konto & Sicherheit</p>
            </div>
            {accountOpen ? (
              <ChevronUp className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
